export const baseUrl = import.meta.env.VITE_API_URL ?? (import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:4100/api/v1' : '/api/v1'));

function loadingStart():void{window.dispatchEvent(new CustomEvent('pivot:loading-start'));}
function loadingEnd():void{window.dispatchEvent(new CustomEvent('pivot:loading-end'));}
function loadingReset():void{window.dispatchEvent(new CustomEvent('pivot:loading-reset'));}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
    public requestId?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function formatIssues(payload: any): string | undefined {
  if (!Array.isArray(payload?.issues) || !payload.issues.length) return undefined;
  return payload.issues
    .map((issue: any) => {
      const field = Array.isArray(issue.path) && issue.path.length ? issue.path.join('.') : 'field';
      return `${field}: ${issue.message ?? 'invalid value'}`;
    })
    .join('; ');
}

const authenticationPaths = new Set(['/auth/login','/auth/driver/login','/auth/saas-admin/login']);
const publicRecoveryPaths = new Set(['/auth/saas-admin/forgot-password','/auth/saas-admin/reset-password']);

function clearStoredSession():void {
  localStorage.removeItem('pivot_token');
  localStorage.removeItem('pivot_user');
  localStorage.removeItem('pivot_branch_id');
  localStorage.removeItem('pivot_login_context');
  localStorage.removeItem('pivot_workspace');
  localStorage.removeItem('pivot_managed_by_platform');
  localStorage.removeItem('pivot_platform_actor_name');
  sessionStorage.removeItem('pivot_platform_session_snapshot');
}

function handleAccessFailure(path:string,status:number,payload:unknown,hadToken:boolean):void {
  if(status===402){
    const storedContext=localStorage.getItem('pivot_login_context');
    const loginContext=storedContext==='platform'?'platform':storedContext==='driver'?'driver':'workspace';
    clearStoredSession();
    loadingReset();
    window.dispatchEvent(new CustomEvent('pivot:subscription-expired',{detail:{payload,loginContext,redirect:window.location.pathname+window.location.search+window.location.hash}}));
    return;
  }
  // A rejected login is not an expired session. Keep the form open and show its normal validation error.
  if(status!==401||!hadToken||authenticationPaths.has(path)||publicRecoveryPaths.has(path))return;
  const storedContext=localStorage.getItem('pivot_login_context');
  const loginContext=storedContext==='platform'?'platform':storedContext==='driver'?'driver':'workspace';
  const redirect=window.location.pathname+window.location.search+window.location.hash;
  clearStoredSession();
  loadingReset();
  window.dispatchEvent(new CustomEvent('pivot:session-expired',{detail:{loginContext,redirect,payload}}));
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('pivot_token');
  const activeBranch = localStorage.getItem('pivot_branch_id');
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 30_000);
  loadingStart();
  try {
    const headers = new Headers(options.headers);
    if (typeof options.body === 'string' && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (activeBranch) headers.set('x-branch-id', activeBranch);
    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      signal: options.signal ?? controller.signal,
      headers
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const issueMessage = formatIssues(payload);
      const message = issueMessage ? `${payload.message ?? 'Validation failed'}: ${issueMessage}` : (payload.message ?? 'Request failed');
      handleAccessFailure(path,response.status,payload,Boolean(token));
      throw new ApiError(message, response.status, payload, payload.requestId);
    }
    return payload as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') throw new ApiError('The request timed out. Check that the API is running and try again.', 408);
    throw new ApiError(error instanceof Error ? error.message : 'Could not reach the server.', 0);
  } finally {
    window.clearTimeout(timer);
    loadingEnd();
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  download,
  preview
};

async function download(path: string, filename?: string): Promise<void> {
  loadingStart();
  try {
    const token = localStorage.getItem('pivot_token');
    const activeBranch = localStorage.getItem('pivot_branch_id');
    const headers: Record<string,string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (activeBranch) headers['x-branch-id'] = activeBranch;
    const response = await fetch(`${baseUrl}${path}`, { headers });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      handleAccessFailure(path,response.status,payload,Boolean(token));
      throw new ApiError(payload.message ?? 'Document download failed', response.status, payload, payload.requestId);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const name = filename ?? match?.[1] ?? 'document.pdf';
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  } finally { loadingEnd(); }
}

async function preview(path:string):Promise<void>{
  loadingStart();
  const target=window.open('about:blank','_blank');
  const token=localStorage.getItem('pivot_token');
  const activeBranch=localStorage.getItem('pivot_branch_id');
  const headers:Record<string,string>={};
  if(token)headers.Authorization=`Bearer ${token}`;
  if(activeBranch)headers['x-branch-id']=activeBranch;
  try{
    const response=await fetch(`${baseUrl}${path}`,{headers});
    if(!response.ok){
      const payload=await response.json().catch(()=>({}));
      handleAccessFailure(path,response.status,payload,Boolean(token));
      throw new ApiError(payload.message??'Document preview failed',response.status,payload,payload.requestId);
    }
    const blob=await response.blob();const url=URL.createObjectURL(blob);
    if(target)target.location.href=url;else window.open(url,'_blank');
    window.setTimeout(()=>URL.revokeObjectURL(url),120_000);
  }catch(error){if(target)target.close();throw error;}finally{loadingEnd();}
}
